<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";

import { AGENT_CONFIG_MAP } from "~/constants/agents/configs";
import {
  DEFAULT_LOCAL_AGENT,
  getLocalAgentDisplay,
  LOCAL_AGENT_DISPLAY,
  LOCAL_AGENT_KEY,
} from "~/constants/localAgent";
import { USER_KIND_CUSTOM, USER_KIND_USER } from "~/constants/userKinds";
import { ExecutionMode } from "~/shared/enums";
import type { User } from "~/shared/types";
import { usePageStore } from "~/stores";
import { BLACK_HEX, getChipColorsHex, WHITE_HEX } from "~/utils/color";
import { getUserInitials } from "~/utils/user";

const props = defineProps<{
  user?: User;
  showAgentWorking?: boolean;
  // TODO remove these props and handle tenant more nicely
  abrev?: string;
  colorHex?: string;
  imageUrl?: string | null;
  // END TODO
  circle?: boolean;
  isContrast?: boolean;
}>();

const pageStore = usePageStore();

const isUser = computed(() => !!props.user);
const isAgent = computed(() => props.user && props.user.kind !== USER_KIND_USER);
const isNonNativeAgent = computed(() => isAgent.value && props.user?.kind !== USER_KIND_CUSTOM);
const isLocalAgent = computed(
  () =>
    isAgent.value && props.user?.kind === USER_KIND_CUSTOM && props.user.agent?.executionMode === ExecutionMode.LOCAL
);
const agentConfig = computed(() => props.user && AGENT_CONFIG_MAP.get(props.user.kind));
const nonNativeAgentIcon = computed(() => (!props.user ? undefined : agentConfig.value?.agentIcon));
const localAgentDisplay = computed(() => {
  if (!isLocalAgent.value) {
    return null;
  }
  return getLocalAgentDisplay(props.user?.agent?.config?.[LOCAL_AGENT_KEY]) ?? LOCAL_AGENT_DISPLAY[DEFAULT_LOCAL_AGENT];
});
const localAgentAvatarIcon = computed(() => localAgentDisplay.value?.avatarIcon);
const circle = computed(() => props.circle || (isUser.value && !isAgent.value));
const workingColor = computed(() => {
  if (agentConfig.value?.color) {
    return agentConfig.value.color;
  }
  return props.user?.colorHex;
});
const abrev = computed(() => {
  if (props.abrev) {
    return props.abrev;
  }
  if (props.user) {
    return getUserInitials(props.user);
  }
  return "F";
});
const colorHex = computed(() => props.colorHex ?? props.user?.colorHex);
const imageUrl = computed(() => props.imageUrl ?? props.user?.imageUrl ?? undefined);

const logoLoadFailed = ref(false);
const hasLogo = computed(() => !logoLoadFailed.value && imageUrl.value);

const hasColor = computed(() => !!colorHex.value);
const borderWidth = ref(1);
const wrapperStyle = computed(() => {
  const result = {
    borderWidth: `${borderWidth.value}px`,
  };
  if (!colorHex.value) {
    return result;
  }
  const color = getChipColorsHex(colorHex.value, pageStore.theme);
  const colorWithContrast = props.isContrast
    ? {
        backgroundColor: WHITE_HEX,
        color: BLACK_HEX,
        borderColor: BLACK_HEX,
      }
    : {
        backgroundColor: color.back,
        color: color.fore,
        borderColor: color.border,
      };
  return { ...result, ...colorWithContrast };
});

// Resize text to fit wrapper.
const text = ref<HTMLSpanElement | null>(null);
const wrapper = ref<HTMLDivElement | null>(null);
const resize = () => {
  if (!text.value) {
    return;
  }
  text.value.style.transform = "";
  nextTick(() => {
    const wrapperWidth = wrapper.value?.offsetWidth;
    const wrapperHeight = wrapper.value?.offsetHeight;
    if (!wrapperWidth || !wrapperHeight || !text.value) {
      return;
    }
    let wrapperDiam = Math.min(wrapperWidth, wrapperHeight);

    borderWidth.value = Math.max(Math.floor(wrapperDiam * 0.05), 1);
    wrapperDiam -= borderWidth.value * 2;

    const textWidth = text.value.offsetWidth;
    const textHeight = text.value.offsetHeight;
    if (!textWidth || !textHeight) {
      return;
    }
    const textDiam = (textWidth ** 2 + textHeight ** 2) ** 0.5;
    const scale = wrapperDiam / textDiam;
    text.value.style.transform = `matrix(${scale},0,0,${scale},0,0)`;
  });
};

// Resize if wrapper or abbreviation change.
useResizeObserver(wrapper, resize);
watch(abrev, resize);
</script>

<template>
  <div
    class="relative bg-std"
    :class="[
      circle ? 'rounded-full' : 'rounded-[25%]',
      isAgent &&
        showAgentWorking &&
        'motion-safe:avatar-shimmer motion-reduce:ring-2 motion-reduce:ring-[var(--avatar-shimmer-color)]',
    ]"
    :style="
      isAgent && showAgentWorking
        ? { '--avatar-shimmer-color': workingColor ?? 'var(--color-recommendation-base)' }
        : undefined
    ">
    <img
      v-if="hasLogo"
      :alt="abrev"
      :src="imageUrl"
      :class="[
        circle ? 'rounded-full' : 'rounded-[25%]',
        isUser && 'border border-md',
        isAgent && showAgentWorking && 'motion-safe:border-2 motion-safe:border-transparent motion-safe:opacity-50',
      ]"
      class="pointer-events-none size-full object-cover select-none"
      @error="logoLoadFailed = true" />
    <div
      v-else-if="(isNonNativeAgent && nonNativeAgentIcon) || localAgentAvatarIcon"
      ref="wrapper"
      class="flex size-full items-center justify-center rounded-[25%]"
      :class="[
        isUser && 'border border-md',
        isAgent &&
          showAgentWorking &&
          'motion-safe:overflow-hidden motion-safe:border-2 motion-safe:border-transparent motion-safe:opacity-50',
      ]">
      <component
        :is="nonNativeAgentIcon ?? localAgentAvatarIcon"
        class="size-full rounded-[25%]"
        :class="isAgent && showAgentWorking && 'motion-safe:rounded-[20%]'" />
    </div>
    <div
      v-else
      ref="wrapper"
      :class="[
        circle ? 'rounded-full' : 'rounded-[25%]',
        isAgent && showAgentWorking && 'motion-safe:border-2 motion-safe:border-transparent motion-safe:opacity-50',
      ]"
      class="flex size-full items-center justify-center border border-md"
      :style="wrapperStyle">
      <span
        ref="text"
        :class="[!hasColor && 'text-vlt']"
        class="pointer-events-none text-sm font-normal whitespace-nowrap select-none">
        {{ abrev }}
      </span>
    </div>
  </div>
</template>
